/**
 * T.C. KİMLİK NUMARASI DOĞRULAMA
 *
 * ================================================================
 * NEDEN SÖZLEŞMEDE, İKİ YERDE DEĞİL
 * ================================================================
 * Hem sunucu (kayıt ve giriş) hem panel (form) aynı kuralı uygulamak
 * zorunda. Bu depoda aynı kuralın iki eve yazılması bir günde **sekiz**
 * sapma üretti; dokuzuncusunu oluşmadan kapatıyoruz.
 *
 * Panelin kendi kontrolü yapması **kullanıcıya hızlı geri bildirim**
 * için; **karar sunucuda**. İstemci doğrulaması bir kolaylıktır,
 * güvenlik sınırı değildir.
 *
 * ================================================================
 * BU BİR "GERÇEK KİŞİ VAR MI" DOĞRULAMASI DEĞİL
 * ================================================================
 * Algoritma yalnızca **sayının kendi içinde tutarlı** olduğunu söyler.
 * Rastgele yazılmış on bir haneyi eler; ama geçerli bir sağlama
 * üretmek de mümkündür. NVİ (Nüfus ve Vatandaşlık İşleri) kimlik
 * doğrulama servisi ayrı bir iştir ve **yapılmadı** — bu modül onun
 * yerine geçmez.
 *
 * Yani: "kafasına göre rastgele numara giremesin" sağlanıyor;
 * "bu numara gerçekten bu kişiye ait" sağlanmıyor.
 */

/** T.C. kimlik numarası her zaman 11 hane. */
export const TC_KIMLIK_UZUNLUK = 11;

export type TcGecersizlikNedeni =
  "BOS" | "UZUNLUK" | "RAKAM_DISI" | "SIFIRLA_BASLAMAZ" | "SAGLAMA_10" | "SAGLAMA_11";

/** Kullanıcıya gösterilecek metinler — tek yerde, ekran uydurmasın. */
export const TC_GECERSIZLIK_METNI: Record<TcGecersizlikNedeni, string> = {
  BOS: "T.C. kimlik numarası boş olamaz.",
  UZUNLUK: `T.C. kimlik numarası ${TC_KIMLIK_UZUNLUK} haneli olmalı.`,
  RAKAM_DISI: "T.C. kimlik numarası yalnızca rakamlardan oluşur.",
  SIFIRLA_BASLAMAZ: "T.C. kimlik numarası sıfırla başlayamaz.",
  SAGLAMA_10: "T.C. kimlik numarası geçersiz — onuncu hane tutmuyor.",
  SAGLAMA_11: "T.C. kimlik numarası geçersiz — son hane tutmuyor.",
};

export type TcSonuc = { gecerli: true } | { gecerli: false; neden: TcGecersizlikNedeni };

/**
 * T.C. kimlik numarasının **kendi içinde tutarlı** olup olmadığı.
 *
 * Kural (NVİ'nin yayımladığı sağlama):
 *   1. 11 hane, yalnızca rakam, ilk hane 0 olamaz.
 *   2. (1., 3., 5., 7., 9. hanelerin toplamı × 7 − 2., 4., 6., 8.
 *      hanelerin toplamı) mod 10 = 10. hane
 *   3. İlk 10 hanenin toplamı mod 10 = 11. hane
 *
 * **Neden `neden` dönüyor:** yalnızca `boolean` dönseydi ekran "geçersiz"
 * derdi ve kullanıcı ONU DÜZELTEMEZDİ — on bir hane yazan biri
 * "sıfırla başlayamaz" ile "sağlama tutmuyor" arasındaki farkı bilmek
 * zorunda. Bu depoda aynı ders `Zod`un İngilizce varsayılan mesajında
 * öğrenildi.
 */
export function tcKimlikDogrula(ham: string): TcSonuc {
  const t = ham.trim();
  if (t.length === 0) return { gecerli: false, neden: "BOS" };
  if (!/^\d+$/.test(t)) return { gecerli: false, neden: "RAKAM_DISI" };
  if (t.length !== TC_KIMLIK_UZUNLUK) return { gecerli: false, neden: "UZUNLUK" };
  if (t[0] === "0") return { gecerli: false, neden: "SIFIRLA_BASLAMAZ" };

  const h = [...t].map((c) => Number(c));
  const tek = h[0]! + h[2]! + h[4]! + h[6]! + h[8]!;
  const cift = h[1]! + h[3]! + h[5]! + h[7]!;

  /*
    `% 10` NEGATİF OLABİLİR: JavaScript'te `-3 % 10 === -3`. `tek*7`
    küçük ve `cift` büyükse fark eksiye düşer ve karşılaştırma sessizce
    yanlış sonuç verir. `+ 10) % 10` bunu kapatıyor.

    Bu satır ölçülmeden yazılsaydı hata YALNIZCA belirli numaralarda
    çıkardı — yani testte kolayca ıskalanırdı.
  */
  const onuncu = (((tek * 7 - cift) % 10) + 10) % 10;
  if (onuncu !== h[9]) return { gecerli: false, neden: "SAGLAMA_10" };

  const ilkOn = h.slice(0, 10).reduce((a, b) => a + b, 0);
  if (ilkOn % 10 !== h[10]) return { gecerli: false, neden: "SAGLAMA_11" };

  return { gecerli: true };
}

/** Kısa yol — mesaj gerekmiyorsa. */
export function tcKimlikGecerliMi(ham: string): boolean {
  return tcKimlikDogrula(ham).gecerli;
}

/**
 * Denetim kaydında ve ekranda gösterilecek maskeli hâl.
 *
 * T.C. kimlik numarası **özel nitelikli kişisel veri**; tam hâliyle
 * loga, ekrana ya da hata mesajına yazılmaz (F-LOG-12 ile aynı gerekçe).
 * Son iki hane bırakılıyor ki destek konuşmasında "hangi kayıt"
 * sorusu cevaplanabilsin.
 */
export function tcMaskele(ham: string): string {
  const t = ham.trim();
  if (t.length !== TC_KIMLIK_UZUNLUK) return "•".repeat(9) + "••";
  return "•".repeat(9) + t.slice(9);
}
